import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/** Curated emoji set, grouped by category.
 *
 *  We deliberately keep this small and dependency-free — a chat
 *  composer needs the ~150 emojis people actually reach for, not the
 *  Unicode 15 long tail. Adding ``emoji-picker-react`` would balloon
 *  the bundle by ~250 KB and force a noscript fallback for a feature
 *  that should be near-zero cost.
 *
 *  Search matches against keywords concatenated to each emoji so a
 *  single hit like "fire" or "love" returns the obvious candidates
 *  even if the user can't remember which category it lives in.
 */
interface EmojiEntry {
  e: string;
  // Space-separated keywords; lowercased. Searched as a substring match.
  k: string;
}

const CATEGORIES: { label: string; items: EmojiEntry[] }[] = [
  {
    label: "Smileys",
    items: [
      { e: "😀", k: "grin smile happy" },
      { e: "😃", k: "happy smile big" },
      { e: "😄", k: "happy smile" },
      { e: "😁", k: "grin beam" },
      { e: "😆", k: "laugh squint" },
      { e: "😅", k: "sweat smile relief" },
      { e: "🤣", k: "lol rolling laugh" },
      { e: "😂", k: "joy tears laugh" },
      { e: "🙂", k: "slight smile" },
      { e: "😉", k: "wink" },
      { e: "😊", k: "blush smile" },
      { e: "😇", k: "innocent halo" },
      { e: "🥰", k: "love hearts smile" },
      { e: "😍", k: "heart eyes love" },
      { e: "🤩", k: "star eyes wow amazing" },
      { e: "😘", k: "kiss" },
      { e: "😗", k: "kiss" },
      { e: "😋", k: "yum tongue" },
      { e: "😛", k: "tongue silly" },
      { e: "😜", k: "wink tongue silly" },
      { e: "🤪", k: "crazy wild" },
      { e: "🤨", k: "skeptical raised brow" },
      { e: "🧐", k: "monocle inspect" },
      { e: "🤓", k: "nerd glasses" },
      { e: "😎", k: "cool sunglasses" },
      { e: "🥳", k: "party celebrate" },
      { e: "😏", k: "smirk sly" },
      { e: "😒", k: "unamused meh" },
      { e: "🙄", k: "eyeroll annoyed" },
      { e: "😬", k: "grimace yikes" },
      { e: "🤥", k: "lying nose" },
      { e: "😴", k: "sleep sleepy" },
      { e: "😪", k: "sleepy tired" },
      { e: "😵", k: "dizzy ko" },
      { e: "🤐", k: "zip mouth quiet" },
      { e: "🤔", k: "thinking hmm" },
      { e: "🤗", k: "hug" },
      { e: "🤭", k: "oops shy" },
      { e: "🫡", k: "salute respect" },
      { e: "😶", k: "blank face" },
      { e: "😐", k: "neutral" },
      { e: "😑", k: "expressionless" },
      { e: "😯", k: "surprise" },
      { e: "😦", k: "frown surprise" },
      { e: "😧", k: "anguished" },
      { e: "😮", k: "wow surprise" },
      { e: "😲", k: "astonished" },
      { e: "🥺", k: "pleading sad" },
      { e: "😢", k: "cry sad" },
      { e: "😭", k: "sob cry" },
      { e: "😤", k: "huff steam" },
      { e: "😠", k: "angry mad" },
      { e: "😡", k: "rage angry mad" },
      { e: "🤬", k: "swear curse mad" },
      { e: "🤯", k: "mind blown" },
      { e: "😳", k: "blush flushed" },
      { e: "🥵", k: "hot heat" },
      { e: "🥶", k: "cold freeze" },
      { e: "😱", k: "scream shock" },
      { e: "😨", k: "fear scared" },
      { e: "😰", k: "anxious sweat" },
      { e: "😥", k: "sad disappoint" },
      { e: "🤤", k: "drool yum" },
      { e: "🤢", k: "nausea sick" },
      { e: "🤮", k: "vomit sick" },
      { e: "🤧", k: "sneeze sick" },
      { e: "🤒", k: "fever sick" },
    ],
  },
  {
    label: "Gestures",
    items: [
      { e: "👍", k: "thumbs up like yes ok" },
      { e: "👎", k: "thumbs down dislike no" },
      { e: "👌", k: "ok perfect" },
      { e: "🤌", k: "italian pinch" },
      { e: "🤏", k: "small pinch" },
      { e: "✌️", k: "peace victory" },
      { e: "🤞", k: "fingers crossed luck" },
      { e: "🤟", k: "love rock" },
      { e: "🤘", k: "rock metal" },
      { e: "🤙", k: "call shaka" },
      { e: "👈", k: "left point" },
      { e: "👉", k: "right point" },
      { e: "👆", k: "up point" },
      { e: "👇", k: "down point" },
      { e: "☝️", k: "up index" },
      { e: "👋", k: "wave hi bye" },
      { e: "🤚", k: "raised back of hand stop" },
      { e: "✋", k: "stop high five" },
      { e: "🖐️", k: "five fingers" },
      { e: "🫶", k: "heart hands love" },
      { e: "👏", k: "clap applause" },
      { e: "🙌", k: "praise hooray" },
      { e: "🤝", k: "handshake deal" },
      { e: "🙏", k: "pray thanks please" },
      { e: "💪", k: "muscle strong" },
      { e: "🦾", k: "robot arm" },
    ],
  },
  {
    label: "Hearts",
    items: [
      { e: "❤️", k: "heart love red" },
      { e: "🧡", k: "heart orange" },
      { e: "💛", k: "heart yellow" },
      { e: "💚", k: "heart green" },
      { e: "💙", k: "heart blue" },
      { e: "💜", k: "heart purple" },
      { e: "🖤", k: "heart black" },
      { e: "🤍", k: "heart white" },
      { e: "🤎", k: "heart brown" },
      { e: "💖", k: "heart sparkle" },
      { e: "💗", k: "heart growing" },
      { e: "💓", k: "heart beating" },
      { e: "💞", k: "hearts revolving" },
      { e: "💕", k: "two hearts love" },
      { e: "💘", k: "heart arrow cupid" },
      { e: "💝", k: "heart ribbon gift" },
      { e: "💔", k: "broken heart" },
    ],
  },
  {
    label: "Misc",
    items: [
      { e: "🔥", k: "fire hot lit" },
      { e: "✨", k: "sparkle shine clean" },
      { e: "⭐", k: "star" },
      { e: "🌟", k: "star glow" },
      { e: "💯", k: "100 hundred" },
      { e: "💥", k: "boom bang" },
      { e: "💢", k: "anger" },
      { e: "💦", k: "sweat drops" },
      { e: "💨", k: "dash wind fast" },
      { e: "🎉", k: "party celebrate" },
      { e: "🎊", k: "confetti" },
      { e: "🎁", k: "gift present" },
      { e: "🏆", k: "trophy win" },
      { e: "🥇", k: "gold first" },
      { e: "✅", k: "check ok done" },
      { e: "❌", k: "x cross no fail" },
      { e: "⚠️", k: "warning caution" },
      { e: "❓", k: "question" },
      { e: "❗", k: "exclaim attention" },
      { e: "💡", k: "idea bulb" },
      { e: "🚀", k: "rocket launch fast" },
      { e: "🏠", k: "home house" },
      { e: "🏢", k: "office building" },
      { e: "🔑", k: "key" },
      { e: "📷", k: "camera photo" },
      { e: "📞", k: "phone call" },
      { e: "💬", k: "speech chat" },
      { e: "💰", k: "money cash" },
      { e: "💵", k: "money dollar" },
      { e: "📈", k: "chart up profit" },
      { e: "📉", k: "chart down loss" },
      { e: "🕐", k: "clock time" },
      { e: "👀", k: "eyes looking" },
    ],
  },
];

const FLAT = CATEGORIES.flatMap((c) => c.items);

/** Approximate rendered size — used to clamp the picker inside the
 *  viewport before it has a chance to measure itself. The real height
 *  varies a little with category-tab + search row, but the estimate is
 *  fine for the initial frame; the layout-effect below corrects on the
 *  next pass. */
const PICKER_W = 288; // matches w-72
const PICKER_H_EST = 320;
const VIEWPORT_MARGIN = 8;

export function EmojiPicker({
  open,
  onClose,
  onPick,
  triggerRef,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the emoji character. The composer is expected to
   *  insert it at the textarea cursor position. */
  onPick: (emoji: string) => void;
  /** Element the popover anchors to. The picker is portalled to
   *  ``document.body`` and positioned ``fixed`` against this trigger,
   *  so it never gets clipped by parents that have ``overflow: hidden``
   *  (chat drawer, scrollers, etc.) and always stays inside the
   *  viewport. */
  triggerRef: RefObject<HTMLElement | null>;
}) {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setTab(0);
  }, [open]);

  /** Compute fixed coordinates from the trigger's rect, centered on
   *  the trigger and clamped to the viewport. We try to place the
   *  picker above the trigger first; if there isn't room, we flip
   *  below. Re-runs on viewport resize so the popover doesn't drift
   *  when the user rotates a phone or resizes the window with the
   *  picker open. */
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const anchor = triggerRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const pop = popoverRef.current;
      const pickerH = pop?.offsetHeight ?? PICKER_H_EST;
      const pickerW = pop?.offsetWidth ?? PICKER_W;

      let left = rect.left + rect.width / 2 - pickerW / 2;
      left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(left, window.innerWidth - pickerW - VIEWPORT_MARGIN),
      );

      let top = rect.top - pickerH - 8;
      if (top < VIEWPORT_MARGIN) {
        top = rect.bottom + 8;
        const maxTop = window.innerHeight - pickerH - VIEWPORT_MARGIN;
        if (top > maxTop) top = Math.max(VIEWPORT_MARGIN, maxTop);
      }
      setPos({ top, left });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (
        popoverRef.current &&
        t &&
        !popoverRef.current.contains(t) &&
        !triggerRef.current?.contains(t)
      ) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, triggerRef]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) return FLAT.filter((it) => it.k.includes(q));
    return CATEGORIES[tab]?.items ?? [];
  }, [search, tab]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: "fixed",
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        visibility: pos ? "visible" : "hidden",
      }}
      className="w-72 rounded-lg bg-ink-900 border border-white/10 shadow-2xl z-[1300] overflow-hidden flex flex-col"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="px-2 pt-2">
        <input
          type="search"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          className="input w-full text-[12px] py-1.5 [&::-webkit-search-cancel-button]:appearance-none"
        />
      </div>
      {!search && (
        <div className="px-2 mt-1 flex gap-0.5 border-b border-white/5">
          {CATEGORIES.map((c, i) => (
            <button
              key={c.label}
              type="button"
              onClick={() => setTab(i)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1.5 ${
                tab === i
                  ? "text-accent-200 border-b-2 border-accent-400 -mb-px"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-8 gap-0.5 p-2 max-h-56 overflow-y-auto">
        {visible.length === 0 ? (
          <div className="col-span-8 text-center text-[11px] text-slate-500 py-4">
            No matches.
          </div>
        ) : (
          visible.map((it) => (
            <button
              key={it.e + it.k}
              type="button"
              onClick={() => onPick(it.e)}
              className="aspect-square text-[18px] leading-none rounded hover:bg-white/[0.06] flex items-center justify-center"
              title={it.k.split(" ")[0]}
            >
              {it.e}
            </button>
          ))
        )}
      </div>
    </div>,
    document.body,
  );
}
